import { format } from "date-fns";
import { X, Check, AlertCircle, BookOpen, Users, FileText, Star, Sparkles, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Notification } from "@servify/shared-schema";

interface NotificationItemProps {
  notification: Notification;
  userRole: 'admin' | 'employee';
  onClose?: () => void;
}

const getNotificationIcon = (type: string, isRead: boolean, isImportant = false) => {
  const iconClass = `h-5 w-5`;
  
  switch (type) {
    case 'module_added':
    case 'content_added':
      return (
        <div className={`relative p-2 rounded-full ${isRead ? 'bg-servify-white-grey' : 'bg-servify-purple'}`}>
          <BookOpen className={`${iconClass} ${isRead ? 'text-servify-purple' : 'text-servify-white-grey'}`} />
          {isImportant && <Star className="absolute -top-1 -right-1 h-3 w-3 text-servify-green" />}
        </div>
      );
    case 'team_media_added':
      return (
        <div className={`p-2 rounded-full ${isRead ? 'bg-servify-white-grey' : 'bg-servify-green'}`}>
          <Users className={`${iconClass} ${isRead ? 'text-servify-green' : 'text-servify-white-grey'}`} />
        </div>
      );
    case 'quiz_completed':
    case 'feedback_submitted':
      return (
        <div className={`p-2 rounded-full ${isRead ? 'bg-servify-white-grey' : 'bg-servify-purple'}`}>
          <FileText className={`${iconClass} ${isRead ? 'text-servify-purple' : 'text-servify-white-grey'}`} />
        </div>
      );
    case 'activity_tracked':
    case 'user_registered':
      return (
        <div className={`p-2 rounded-full ${isRead ? 'bg-servify-white-grey' : 'bg-servify-alt-green'}`}>
          <AlertCircle className={`${iconClass} ${isRead ? 'text-servify-alt-green' : 'text-servify-white-grey'}`} />
        </div>
      );
    default:
      return (
        <div className={`p-2 rounded-full ${isRead ? 'bg-servify-light-grey' : 'bg-servify-medium-grey'}`}>
          <AlertCircle className={`${iconClass} ${isRead ? 'text-servify-medium-grey' : 'text-servify-white-grey'}`} />
        </div>
      );
  }
};

const getNotificationStyle = (type: string, isRead: boolean) => {
  if (isRead) {
    return 'bg-servify-white-grey/60 border-servify-light-grey/60 backdrop-blur-sm';
  }
  
  // Use Servify colors for all notification types
  return 'bg-gradient-to-br from-servify-purple/5 via-servify-white-grey to-servify-purple/3 border-servify-purple/30 shadow-md shadow-servify-purple/10';
};

const getPriorityLevel = (type: string, createdAt: string | Date | null) => {
  if (!createdAt) return 'low';
  
  const now = new Date();
  const notificationTime = new Date(createdAt);
  const hoursDiff = (now.getTime() - notificationTime.getTime()) / (1000 * 60 * 60);
  
  // Recent notifications (< 1 hour) are high priority
  if (hoursDiff < 1) return 'high';
  // Medium priority for notifications < 24 hours
  if (hoursDiff < 24) return 'medium';
  // Low priority for older notifications
  return 'low';
};

export function NotificationItem({ notification, userRole, onClose }: NotificationItemProps) {
  const priority = getPriorityLevel(notification.type, notification.createdAt);
  const isImportant = priority === 'high';
  
  const handleMarkAsRead = async () => {
    if (notification.isRead) return;
    
    try {
      // Instead of just marking as read, delete the notification to remove it from UI
      await apiRequest('DELETE', `/notifications/${notification.id}`);
      queryClient.invalidateQueries({ queryKey: ['/notifications/unread'] });
    } catch (error) {
      console.error('Error removing notification:', error);
    }
  };

  const handleDismiss = async () => {
    try {
      await apiRequest('DELETE', `/notifications/${notification.id}`);
      queryClient.invalidateQueries({ queryKey: ['/notifications/unread'] });
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  return (
    <Card 
      className={`
        relative p-4 cursor-pointer overflow-hidden
        border rounded-xl backdrop-blur-sm
        ${getNotificationStyle(notification.type, notification.isRead ?? false)}
        hover:shadow-lg
      `}
      onClick={handleMarkAsRead}
      data-testid={`notification-item-${notification.id}`}
    >
      {/* Priority indicator stripe */}
      {!(notification.isRead ?? true) && (
        <div className={`
          absolute left-0 top-0 w-1 h-full 
          ${priority === 'high' ? 'bg-servify-green' : ''}
          ${priority === 'medium' ? 'bg-servify-alt-green' : ''}
          ${priority === 'low' ? 'bg-servify-purple' : ''}
        `} />
      )}
      
      {/* Sparkles for important notifications */}
      {isImportant && !(notification.isRead ?? true) && (
        <Sparkles className="absolute top-2 right-2 h-4 w-4 text-servify-green" />
      )}
      
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 relative">
          {getNotificationIcon(notification.type, notification.isRead ?? false, isImportant)}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className={`
                  text-sm font-semibold line-clamp-2
                  ${!notification.isRead ? 'text-servify-dark-grey' : 'text-servify-medium-grey'}
                `}>
                  {notification.title}
                </h4>
                {!(notification.isRead ?? true) && (
                  <ArrowRight className="h-3 w-3 text-servify-light-grey" />
                )}
              </div>
              
              <p className={`
                text-sm mt-1 line-clamp-2
                ${!(notification.isRead ?? true) ? 'text-servify-medium-grey' : 'text-servify-light-grey'}
              `}>
                {notification.message}
              </p>
              
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-center gap-1 text-xs text-servify-medium-grey">
                  <Clock className="h-3 w-3" />
                  <span>{format(new Date(notification.createdAt || new Date()), 'MMM d, h:mm a')}</span>
                </div>
                
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {!(notification.isRead ?? true) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkAsRead();
                  }}
                  className="h-8 w-8 p-0 rounded-full hover:bg-servify-white-grey"
                  data-testid={`mark-read-${notification.id}`}
                  title="Mark as read"
                >
                  <Check className="h-4 w-4 text-servify-green" />
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismiss();
                }}
                className="h-8 w-8 p-0 rounded-full hover:bg-servify-white-grey"
                data-testid={`dismiss-${notification.id}`}
                title="Dismiss"
              >
                <X className="h-4 w-4 text-servify-medium-grey" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Removed shimmer effect */}
    </Card>
  );
}